import { useEffect, useState } from "react";
import { Container } from "./Container";
import { Button } from "./Button";
import { cn } from "../lib/cn";
import { handleSectionLink } from "../lib/scrollToSection";
import { siteConfig } from "../data/portfolio";

const NAV_HREFS = siteConfig.navigation.map((item) => item.href);

/** Sticky translucent header: monogram tile, pill nav, contact CTA. */
export function SiteHeader() {
  const active = useActiveSection(NAV_HREFS);

  return (
    <header className="border-hairline bg-header sticky top-0 z-50 border-b backdrop-blur-xl">
      <Container className="flex h-18 items-center justify-between gap-4">
        <a
          href="#top"
          aria-label={`${siteConfig.name} home`}
          onClick={(event) => handleSectionLink(event, "#top")}
          className="text-strong inline-flex items-center gap-3"
        >
          <span className="bg-inverse text-on-inverse grid size-10 place-items-center rounded-xl text-sm font-bold tracking-tight">
            {siteConfig.initials}
          </span>
          <span className="text-sm font-semibold">{siteConfig.name}</span>
        </a>

        <nav
          aria-label="Primary navigation"
          className="hidden items-center gap-1 md:flex"
        >
          {siteConfig.navigation.map((item) => (
            <a
              key={item.href}
              href={item.href}
              aria-current={active === item.href ? "true" : undefined}
              onClick={(event) => handleSectionLink(event, item.href)}
              className={cn(
                "ease-standard rounded-full px-4 py-2 text-sm font-medium transition duration-200",
                active === item.href
                  ? "bg-subtle text-strong"
                  : "text-muted hover:bg-subtle hover:text-strong",
              )}
            >
              {item.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <Button icon="mail" iconPosition="left" size="sm" href="#contact">
            Contact
          </Button>
        </div>
      </Container>
    </header>
  );
}

/** Highlights the last section whose top has passed the sticky header. */
function useActiveSection(hrefs: string[]) {
  const [active, setActive] = useState(hrefs[0]);

  useEffect(() => {
    const onScroll = () => {
      let current = hrefs[0];
      for (const href of hrefs) {
        const element = document.getElementById(href.slice(1));
        if (element && element.getBoundingClientRect().top <= 200) {
          current = href;
        }
      }
      setActive(current);
    };

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [hrefs]);

  return active;
}
